import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SpinnerGap, Warning, DownloadSimple, ArrowLeft } from '@phosphor-icons/react';
import { FilterPanel, FilterField } from '../components/FilterPanel';
import { ExportPreviewModal } from '../components/ExportPreviewModal';
import { fmtINR, fmtUSD } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BackoutsPage = () => {
  const navigate = useNavigate();
  const [backouts, setBackouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);
  // Filters
  const [fOrg, setFOrg] = useState('all');
  const [fDateFrom, setFDateFrom] = useState('');
  const [fDateTo, setFDateTo] = useState('');
  const [fSearch, setFSearch] = useState('');
  const [fTeam, setFTeam] = useState('all');
  const [fRecruiter, setFRecruiter] = useState('all');

  useEffect(() => {
    axios.get(`${API_URL}/api/placements/backouts`, { withCredentials: true })
      .then(res => setBackouts(res.data || []))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const orgs = [...new Set(backouts.map(b => b.organization || b.company || '').filter(Boolean))];
  const teams = [...new Set(backouts.map(b => b.team_name || '').filter(Boolean))].sort();
  const recruiters = [...new Set(backouts.map(b => b.created_by_name || '').filter(Boolean))].sort();

  const filtered = backouts.filter(b => {
    if (fOrg !== 'all' && (b.organization || b.company) !== fOrg) return false;
    if (fTeam !== 'all' && b.team_name !== fTeam) return false;
    if (fRecruiter !== 'all' && b.created_by_name !== fRecruiter) return false;
    if (fDateFrom && (b.created_at || '') < fDateFrom) return false;
    if (fDateTo && (b.created_at || '') > fDateTo + 'T23:59:59') return false;
    if (fSearch && !(b.candidate_name || '').toLowerCase().includes(fSearch.toLowerCase()) && !(b.created_by_name || '').toLowerCase().includes(fSearch.toLowerCase())) return false;
    return true;
  });

  const activeFilterCount = [fOrg !== 'all', fTeam !== 'all', fRecruiter !== 'all', fDateFrom, fDateTo, fSearch].filter(Boolean).length;
  const resetFilters = () => { setFOrg('all'); setFTeam('all'); setFRecruiter('all'); setFDateFrom(''); setFDateTo(''); setFSearch(''); };

  return (
    <div className="space-y-6" data-testid="backouts-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9" data-testid="back-btn"><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Backed Out Placements</h1>
            <p className="text-slate-500 mt-1">Placements that were backed out after approval</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => setShowExport(true)} className="gap-1.5" data-testid="export-backouts-btn">
          <DownloadSimple size={16} /> Export to Excel
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-2xl font-bold text-red-700">{filtered.length}</p>
          <p className="text-sm text-slate-500">Total Backouts</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-lg font-bold text-amber-700">{fmtINR(filtered.reduce((s, b) => s + (b.my_projected_payout ?? b.my_split_amount ?? b.incentives?.my_split ?? b.incentives?.total_placement ?? 0), 0))}</p>
          <p className="text-sm text-slate-500">Lost Incentives</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <p className="text-lg font-bold text-slate-700">{fmtUSD(filtered.reduce((s, b) => s + (b.margin || 0), 0))}</p>
          <p className="text-sm text-slate-500">Lost Margin</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <FilterPanel onApply={() => {}} onReset={resetFilters} activeCount={activeFilterCount}>
        <FilterField label="Search">
          <Input placeholder="Candidate or recruiter name..." value={fSearch} onChange={e => setFSearch(e.target.value)} className="h-9" data-testid="backout-filter-search" />
        </FilterField>
        <FilterField label="Organization">
          <Select value={fOrg} onValueChange={setFOrg}>
            <SelectTrigger className="h-9" data-testid="backout-filter-org"><SelectValue placeholder="All Organizations" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Organizations</SelectItem>
              {orgs.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Date From">
          <Input type="date" value={fDateFrom} onChange={e => setFDateFrom(e.target.value)} className="h-9" data-testid="backout-filter-from" />
        </FilterField>
        <FilterField label="Date To">
          <Input type="date" value={fDateTo} onChange={e => setFDateTo(e.target.value)} className="h-9" data-testid="backout-filter-to" />
        </FilterField>
        <FilterField label="Team">
          <Select value={fTeam} onValueChange={setFTeam}>
            <SelectTrigger className="h-9" data-testid="backout-filter-team"><SelectValue placeholder="All Teams" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {teams.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Recruiter">
          <Select value={fRecruiter} onValueChange={setFRecruiter}>
            <SelectTrigger className="h-9" data-testid="backout-filter-recruiter"><SelectValue placeholder="All Recruiters" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Recruiters</SelectItem>
              {recruiters.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterField>
      </FilterPanel>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><SpinnerGap size={32} className="animate-spin text-blue-600 mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Warning size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No backed out placements found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {['Candidate', 'Recruiter', 'Category', 'Org', 'Bill Rate', 'Pay Rate', 'Margin', 'Lost Incentive', 'Reason', 'Date'].map(h => (
                      <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((b, idx) => (
                    <tr key={idx} className="hover:bg-slate-50" data-testid={`backout-row-${idx}`}>
                      <td className="px-3 py-3">
                        <Link to={`/dashboard/placements/${b.id}`} className="text-blue-600 hover:underline font-medium">{b.candidate_name}</Link>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600">{b.created_by_name}</td>
                      <td className="px-3 py-3 text-xs">{b.category?.replace(/_/g, ' ')}</td>
                      <td className="px-3 py-3"><Badge variant="outline" className="text-[10px]">{b.organization || b.company || '-'}</Badge></td>
                      <td className="px-3 py-3 text-xs">{fmtUSD(b.bill_rate)}</td>
                      <td className="px-3 py-3 text-xs">{fmtUSD(b.pay_rate)}</td>
                      <td className="px-3 py-3 text-xs font-medium">{fmtUSD(b.margin)}</td>
                      <td className="px-3 py-3 text-xs font-bold text-red-700">{fmtINR(b.my_projected_payout ?? b.my_split_amount ?? b.incentives?.my_split ?? b.incentives?.total_placement ?? 0)}</td>
                      <td className="px-3 py-3 text-xs text-slate-600 max-w-[200px] truncate">{b.backout_reason || '-'}</td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap">{b.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ExportPreviewModal open={showExport} onClose={() => setShowExport(false)}
        data={filtered} fileName={`Backouts_${new Date().toISOString().slice(0,10)}`}
        title="Backouts Export Preview"
        columns={[
          { key: 'candidate', header: 'Candidate', accessor: r => r.candidate_name },
          { key: 'recruiter', header: 'Recruiter', accessor: r => r.created_by_name },
          { key: 'category', header: 'Category', accessor: r => r.category?.replace(/_/g, ' ') },
          { key: 'org', header: 'Organization', accessor: r => r.organization || r.company || '' },
          { key: 'bill', header: 'Bill Rate ($)', accessor: r => r.bill_rate },
          { key: 'pay', header: 'Pay Rate ($)', accessor: r => r.pay_rate },
          { key: 'margin', header: 'Margin ($)', accessor: r => (r.margin || 0).toFixed(2) },
          { key: 'incentive', header: 'Lost Incentive (INR)', accessor: r => r.my_projected_payout ?? r.my_split_amount ?? r.incentives?.my_split ?? r.incentives?.total_placement ?? 0 },
          { key: 'reason', header: 'Reason', accessor: r => r.backout_reason || '' },
          { key: 'date', header: 'Date', accessor: r => r.created_at?.slice(0, 10) },
        ]}
      />
    </div>
  );
};

export default BackoutsPage;
